/**
 * Helper Request Details Page
 * Shows request details with integrated chat for helpers
 */

import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { requestsAPI, chatAPI, ratingsAPI, usersAPI } from '../../services/api';
import * as socketService from '../../services/socket';
import { useNotifications } from '../../context/NotificationContext';
import { useAuth } from '../../context/AuthContext';
import {
  ArrowLeft,
  MapPin,
  Calendar,
  Clock,
  Ship,
  Users,
  MessageCircle,
  CheckCircle,
  AlertCircle,
  Send,
  User,
  Star,
  Info,
  X,
  Briefcase,
  UserCheck,
  Award,
  FileEdit,
  ArrowRight,
  Phone,
  Anchor,
  DollarSign,
  UserPlus,
} from 'lucide-react';
import { format } from 'date-fns';

function HelperRequestDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { showToast } = useNotifications();
  const { user } = useAuth();

  const [request, setRequest] = useState(null);
  const [loading, setLoading] = useState(true);

  // Chat state
  const [chatData, setChatData] = useState(null);
  const [chatLoading, setChatLoading] = useState(false);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const messagesEndRef = useRef(null);
  const [unreadCount, setUnreadCount] = useState(0);

  // Completion and rating state
  const [actionLoading, setActionLoading] = useState(false);
  const [showRatingModal, setShowRatingModal] = useState(false);
  const [rating, setRating] = useState(0);
  const [ratingComment, setRatingComment] = useState('');
  const [submittingRating, setSubmittingRating] = useState(false);

  // Application state
  const [applicationMessage, setApplicationMessage] = useState('');
  const [applying, setApplying] = useState(false);

  // Withdraw state
  const [showWithdrawModal, setShowWithdrawModal] = useState(false);
  const [withdrawReason, setWithdrawReason] = useState('');
  const [withdrawing, setWithdrawing] = useState(false);

  // More Info modal state
  const [showMoreInfo, setShowMoreInfo] = useState(false);
  const [moreInfoData, setMoreInfoData] = useState(null);
  const [loadingMoreInfo, setLoadingMoreInfo] = useState(false);

  useEffect(() => {
    loadRequestDetails();
  }, [id]);

  // Socket listener for real-time chat updates
  useEffect(() => {
    if (!chatData?.id) return;

    socketService.joinConversation(chatData.id);

    const unsubMessage = socketService.on(socketService.EVENTS.NEW_MESSAGE, (data) => {
      if (data?.conversation_id === chatData.id && data?.message) {
        setMessages(prev => {
          // Avoid duplicates
          if (prev.some(m => m.id === data.message.id)) return prev;
          return [...prev, data.message];
        });
      }
    });

    return () => {
      socketService.leaveConversation(chatData.id);
      unsubMessage();
    };
  }, [chatData?.id]);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    if (messages.length > 0) {
      setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
    }
  }, [messages]);

  const loadRequestDetails = async () => {
    try {
      const response = await requestsAPI.getById(id);
      const requestData = response.data || response.request;
      setRequest(requestData);

      // Load chat if helper has been selected for this request
      const isSelected = requestData?.application_status === 'selected';
      const chatAllowed = ['matched', 'in_progress', 'completed', 'active', 'pending'].includes(requestData?.status);

      console.log('Chat check:', {
        owner_id: requestData?.owner_id,
        isSelected,
        status: requestData?.status,
        chatAllowed,
        application_status: requestData?.application_status
      });

      if (requestData?.owner_id && isSelected) {
        loadChat(requestData.owner_id);
      }
    } catch (error) {
      console.error('Failed to load request:', error);
      showToast('Failed to load request details', 'error');
    } finally {
      setLoading(false);
    }
  };

  const loadChat = async (ownerId) => {
    setChatLoading(true);
    try {
      const response = await chatAPI.getRequestConversation(id, ownerId);
      setChatData(response.data);
      const msgs = response.data.messages || [];
      setMessages(msgs);
      const unread = msgs.filter(m => m.sender_id !== user?.id && !(m.read_by || []).includes(user?.id)).length;
      setUnreadCount(unread);
      if (response.data?.id && unread > 0) {
        chatAPI.markAsRead(response.data.id).then(() => setUnreadCount(0));
      }
      // Scroll to bottom after loading messages
      setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'auto' }), 150);
    } catch (error) {
      console.error('Failed to load chat:', error);
    } finally {
      setChatLoading(false);
    }
  };

  const handleSendMessage = async () => {
    console.log('handleSendMessage called', { newMessage, sendingMessage, chatData: chatData?.id });
    if (!newMessage.trim() || sendingMessage || !chatData) {
      console.log('Early return:', { empty: !newMessage.trim(), sending: sendingMessage, noChat: !chatData });
      return;
    }

    setSendingMessage(true);
    const messageContent = newMessage.trim();
    setNewMessage('');

    try {
      console.log('Sending message to conversation:', chatData.id, 'content:', messageContent);
      const response = await chatAPI.sendMessage(chatData.id, messageContent);
      console.log('Message sent successfully:', response);
      setMessages(prev => [...prev, response.data]);
      setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
    } catch (error) {
      console.error('Failed to send message:', error);
      setNewMessage(messageContent);
      showToast('Failed to send message', 'error');
    } finally {
      setSendingMessage(false);
    }
  };

  const handleMarkComplete = async () => {
    // Show rating modal first - combine mark complete with rating
    setShowRatingModal(true);
  };

  const handleCompleteAndRate = async () => {
    if (rating === 0) {
      showToast('Please select a rating', 'error');
      return;
    }
    setSubmittingRating(true);
    try {
      const response = await requestsAPI.completeAndRate(id, rating, ratingComment);
      if (response.data?.fully_completed) {
        showToast('Job completed! Thank you for your rating.', 'success');
      } else {
        showToast('Marked as complete with rating. Waiting for owner to confirm.', 'success');
      }
      setShowRatingModal(false);
      setRating(0);
      setRatingComment('');
      loadRequestDetails();
    } catch (error) {
      showToast(error.message || 'Failed to complete', 'error');
    } finally {
      setSubmittingRating(false);
    }
  };

  const handleSubmitRating = async () => {
    if (rating === 0) {
      showToast('Please select a rating', 'error');
      return;
    }
    setSubmittingRating(true);
    try {
      await ratingsAPI.submit(id, rating, ratingComment);
      showToast('Thank you for your rating!', 'success');
      setShowRatingModal(false);
      loadRequestDetails();
    } catch (error) {
      showToast(error.message || 'Failed to submit rating', 'error');
    } finally {
      setSubmittingRating(false);
    }
  };

  const handleApply = async () => {
    setApplying(true);
    try {
      await requestsAPI.applyToRequest(id, applicationMessage);
      showToast('Application sent successfully!', 'success');
      setApplicationMessage('');
      loadRequestDetails();
    } catch (error) {
      showToast(error.message || 'Failed to apply', 'error');
    } finally {
      setApplying(false);
    }
  };

  const handleWithdraw = async () => {
    setWithdrawing(true);
    try {
      await requestsAPI.withdrawApplication(id, withdrawReason || undefined);
      showToast('You have withdrawn from this job.', 'success');
      setShowWithdrawModal(false);
      setWithdrawReason('');
      navigate('/helper/requests');
    } catch (error) {
      showToast(error.message || 'Failed to withdraw', 'error');
    } finally {
      setWithdrawing(false);
    }
  };

  const handleShowMoreInfo = async () => {
    if (!request?.owner_id) return;
    setShowMoreInfo(true);
    setLoadingMoreInfo(true);
    try {
      const response = await usersAPI.getPublicProfile(request.owner_id);
      setMoreInfoData(response.data);
    } catch (error) {
      showToast('Failed to load profile', 'error');
      setShowMoreInfo(false);
    } finally {
      setLoadingMoreInfo(false);
    }
  };

  const getStatusInfo = (status) => {
    const info = {
      pending: { color: 'yellow', label: 'Pending' },
      active: { color: 'yellow', label: 'Open' },
      matched: { color: 'green', label: 'Matched' },
      confirmed: { color: 'green', label: 'Confirmed' },
      in_progress: { color: 'blue', label: 'In Progress' },
      completed: { color: 'gray', label: 'Completed' },
      cancelled: { color: 'red', label: 'Cancelled' },
    };
    return info[status] || info.pending;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner w-8 h-8" />
      </div>
    );
  }

  if (!request) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="h-16 w-16 text-gray-300 mx-auto mb-4" />
        <h2 className="text-xl font-medium text-navy-900">Request not found</h2>
        <button onClick={() => navigate('/helper/requests')} className="btn-primary mt-4">
          Back to Requests
        </button>
      </div>
    );
  }

  const statusInfo = getStatusInfo(request.status);
  // Chat enabled when helper is selected - regardless of request status
  const chatEnabled = request.application_status === 'selected';

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div>
        <button
          onClick={() => navigate('/helper/requests')}
          className="flex items-center text-navy-600 hover:text-navy-800 mb-4"
        >
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back to Requests
        </button>

        <div className="flex items-start justify-between">
          <div>
            {/* Request type badge */}
            <div className="mb-2">
              {request.request_category === 'position' ? (
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-gold-100 text-gold-800">
                  <Briefcase className="h-4 w-4 mr-1.5" />
                  Crew Position
                </span>
              ) : (
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-primary-100 text-primary-800">
                  <Anchor className="h-4 w-4 mr-1.5" />
                  Mooring Helper
                </span>
              )}
            </div>
            <h1 className="text-2xl font-display font-bold text-navy-900">
              {request.request_category === 'position' && request.crew_position
                ? request.crew_position.replace(/_/g, ' ').replace(/^(deck|interior|galley|eng)\s/i, '').replace(/\b\w/g, l => l.toUpperCase())
                : request.port_name}
            </h1>
            <div className="flex items-center mt-2 space-x-4 text-navy-600">
              {request.request_category === 'position' ? (
                <>
                  <div className="flex items-center">
                    <MapPin className="h-4 w-4 mr-1" />
                    {request.port_name}
                  </div>
                  <div className="flex items-center">
                    <Calendar className="h-4 w-4 mr-1" />
                    {format(new Date(request.start_date || request.scheduled_date), 'MMM d')} - {request.end_date ? format(new Date(request.end_date), 'MMM d, yyyy') : 'Ongoing'}
                  </div>
                </>
              ) : (
                <>
                  <div className="flex items-center">
                    <Calendar className="h-4 w-4 mr-1" />
                    {format(new Date(request.scheduled_date), 'EEEE, MMM d, yyyy')}
                  </div>
                  <div className="flex items-center">
                    <Clock className="h-4 w-4 mr-1" />
                    {request.eta_from || request.scheduled_time}
                    {request.eta_to && ` - ${request.eta_to}`}
                  </div>
                </>
              )}
            </div>
          </div>
          <div className={`flex items-center px-4 py-2 rounded-full bg-${statusInfo.color}-100 text-${statusInfo.color}-800`}>
            <span className="font-medium">{statusInfo.label}</span>
          </div>
        </div>
      </div>

      <div className={chatEnabled ? "grid md:grid-cols-2 gap-6" : ""}>
        {/* Request Details Card */}
        <div className="card">
          <h2 className="text-lg font-display font-semibold text-navy-900 mb-4">
            Request Details
          </h2>
          <div className="space-y-4">
            <div className="flex items-start">
              <Ship className="h-5 w-5 text-navy-400 mt-0.5 mr-3" />
              <div>
                <p className="text-sm text-navy-500">Yacht</p>
                <p className="font-medium text-navy-900">{request.yacht_name || 'Not specified'}</p>
                <p className="text-sm text-navy-600">
                  {[
                    request.yacht_type && request.yacht_type.replace('_', ' '),
                    (request.length_m || request.yacht_length) && `${request.length_m || request.yacht_length}m`
                  ].filter(Boolean).join(' \u00B7 ') || null}
                </p>
              </div>
            </div>
            <div className="flex items-start">
              <Users className="h-5 w-5 text-navy-400 mt-0.5 mr-3" />
              <div>
                <p className="text-sm text-navy-500">Positions</p>
                <p className="font-medium text-navy-900">{(request.helpers_needed - (request.helpers_confirmed || 0))}/{request.helpers_needed} available</p>
              </div>
            </div>
            {/* Show position info for crew requests, mooring type for mooring requests */}
            {request.request_category === 'position' ? (
              <>
                <div className="flex items-start">
                  <UserPlus className="h-5 w-5 text-gold-500 mt-0.5 mr-3" />
                  <div>
                    <p className="text-sm text-navy-500">Position</p>
                    <p className="font-medium text-navy-900 capitalize">
                      {request.crew_position?.replace(/_/g, ' ').replace(/^(deck|interior|galley|eng)\s/i, '') || 'Not specified'}
                    </p>
                  </div>
                </div>
                {request.crew_department && (
                  <div className="flex items-start">
                    <Briefcase className="h-5 w-5 text-navy-400 mt-0.5 mr-3" />
                    <div>
                      <p className="text-sm text-navy-500">Department</p>
                      <p className="font-medium text-navy-900 capitalize">{request.crew_department}</p>
                    </div>
                  </div>
                )}
                <div className="flex items-start">
                  <Clock className="h-5 w-5 text-navy-400 mt-0.5 mr-3" />
                  <div>
                    <p className="text-sm text-navy-500">Contract Type</p>
                    <p className="font-medium text-navy-900">
                      {request.contract_type === 'permanent' ? 'Full Time' :
                       request.contract_type === 'seasonal' ? 'Seasonal' :
                       request.contract_type === 'temporary' ? 'Daywork' :
                       request.contract_type === 'delivery' ? 'Delivery' :
                       'Temporary'}
                    </p>
                  </div>
                </div>
                <div className="flex items-start">
                  <Calendar className="h-5 w-5 text-navy-400 mt-0.5 mr-3" />
                  <div>
                    <p className="text-sm text-navy-500">Contract Period</p>
                    <p className="font-medium text-navy-900">
                      {format(new Date(request.start_date || request.scheduled_date), 'MMM d, yyyy')} - {request.end_date ? format(new Date(request.end_date), 'MMM d, yyyy') : 'Ongoing'}
                    </p>
                  </div>
                </div>
                {request.salary && (
                  <div className="flex items-start">
                    <DollarSign className="h-5 w-5 text-green-500 mt-0.5 mr-3" />
                    <div>
                      <p className="text-sm text-navy-500">Monthly Salary</p>
                      <p className="font-medium text-green-600">
                        {request.salary_currency === 'EUR' ? '€' : '$'}{request.salary}
                      </p>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="flex items-start">
                <Anchor className="h-5 w-5 text-navy-400 mt-0.5 mr-3" />
                <div>
                  <p className="text-sm text-navy-500">Mooring Type</p>
                  <p className="font-medium text-navy-900 capitalize">
                    {request.mooring_type?.replace('_', ' ') || 'Not specified'}
                  </p>
                </div>
              </div>
            )}
            {request.berth_number && (
              <div className="flex items-start">
                <MapPin className="h-5 w-5 text-navy-400 mt-0.5 mr-3" />
                <div>
                  <p className="text-sm text-navy-500">Berth Number</p>
                  <p className="font-medium text-navy-900">{request.berth_number}</p>
                </div>
              </div>
            )}
          </div>

          {/* Message from Owner */}
          {(request.special_notes || request.notes) && (
            <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-sm font-medium text-amber-800 mb-1">Message from Owner</p>
              <p className="text-sm text-amber-700 italic">
                "{request.special_notes || request.notes}"
              </p>
            </div>
          )}

          {/* Owner Info */}
          <div className="mt-6 pt-4 border-t border-gray-100">
            <p className="text-sm text-navy-500 mb-2">Request by</p>
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                {request.owner_photo ? (
                  <img src={request.owner_photo} alt="" className="h-10 w-10 rounded-full object-cover" />
                ) : (
                  <div className="h-10 w-10 bg-primary-100 rounded-full flex items-center justify-center">
                    <User className="h-5 w-5 text-primary-600" />
                  </div>
                )}
                <div className="ml-3">
                  <p className="font-medium text-navy-900">
                    {request.owner_first_name} {request.owner_last_name}
                  </p>
                  <div className="flex items-center text-sm text-navy-600 mt-0.5">
                    {request.owner_rating > 0 ? (
                      <>
                        <Star className="h-3.5 w-3.5 text-gold-500 fill-current mr-1" />
                        <span>{Number(request.owner_rating).toFixed(1)}</span>
                        {request.owner_rating_count > 0 && (
                          <span className="text-navy-400 ml-1">({request.owner_rating_count} review{request.owner_rating_count !== 1 ? 's' : ''})</span>
                        )}
                      </>
                    ) : (
                      <span className="text-navy-400">New captain</span>
                    )}
                  </div>
                  {/* Show owner phone only after helper is confirmed for the job */}
                  {request.owner_phone && request.application_status === 'selected' && request.status !== 'completed' && request.status !== 'cancelled' && (
                    <div className="flex items-center text-sm mt-1">
                      <a href={`tel:${request.owner_phone}`} className="flex items-center text-primary-600 hover:text-primary-700">
                        <Phone className="h-3.5 w-3.5 mr-1" />
                        {request.owner_phone}
                      </a>
                      {request.owner_whatsapp && (
                        <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-green-100 text-green-700">
                          <svg className="h-3 w-3 mr-1" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/>
                          </svg>
                          I'm on WhatsApp
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </div>
              <button
                onClick={handleShowMoreInfo}
                className="flex items-center text-xs text-primary-600 hover:text-primary-700 bg-primary-50 px-3 py-1.5 rounded-lg"
              >
                <Info className="h-3 w-3 mr-1" />
                More Info
              </button>
            </div>
          </div>
        </div>

        {/* Chat Section - only available after helper selection */}
        {chatEnabled && (
        <div className="card flex flex-col" style={{ minHeight: '400px' }}>
          <h2 className="text-lg font-display font-semibold text-navy-900 mb-4 flex items-center">
            <MessageCircle className="h-5 w-5 mr-2" />
            Chat with Owner
            {unreadCount > 0 && (
              <span className="ml-2 h-2.5 w-2.5 bg-red-500 rounded-full" />
            )}
          </h2>

          {chatLoading ? (
            <div className="flex-1 flex items-center justify-center">
              <div className="spinner w-6 h-6" />
            </div>
          ) : (
            <>
              {/* Messages */}
              <div className="chat-messages flex-1 space-y-3 mb-4 pr-1" style={{ maxHeight: '280px' }}>
                {messages.length === 0 ? (
                  <div className="text-center text-navy-400 py-8">
                    <MessageCircle className="h-10 w-10 mx-auto mb-2 opacity-50" />
                    <p>No messages yet</p>
                    <p className="text-sm">Send a message to the owner</p>
                  </div>
                ) : (
                  messages.map((message) => {
                    // Handle system messages with modification details
                    if (message.message_type === 'system') {
                      let parsed = null;
                      try { parsed = JSON.parse(message.content); } catch (e) { /* not JSON */ }

                      if (parsed?.type === 'request_modified' && parsed.changes) {
                        const changeEntries = Object.values(parsed.changes);
                        return (
                          <div key={message.id} className="flex justify-center">
                            <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 max-w-xs w-full">
                              <div className="flex items-center text-amber-700 font-medium text-sm mb-2">
                                <FileEdit className="h-4 w-4 mr-1.5" />
                                Request Modified
                              </div>
                              <div className="space-y-1">
                                {changeEntries.map((c, idx) => (
                                  <div key={idx} className="text-xs text-navy-700">
                                    <span className="font-medium">{c.label}:</span>{' '}
                                    <span className="text-navy-500">{c.from || '(empty)'}</span>
                                    <ArrowRight className="inline h-3 w-3 mx-1 text-amber-500" />
                                    <span className="text-navy-900 font-medium">{c.to || '(empty)'}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>
                        );
                      }

                      // Fallback for other system messages (plain text)
                      return (
                        <div key={message.id} className="flex justify-center">
                          <div className="text-xs text-navy-400 bg-gray-100 rounded-full px-3 py-1">
                            {message.content}
                          </div>
                        </div>
                      );
                    }

                    const isOwn = message.sender_id === user?.id;
                    return (
                      <div key={message.id} className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}>
                        <div className="max-w-[75%]">
                          <div className={`chat-bubble ${isOwn ? 'sent' : 'received'}`}>
                            {message.content}
                          </div>
                          <p className={`text-xs text-navy-400 mt-1 ${isOwn ? 'text-right' : ''}`}>
                            {message.sent_at ? format(new Date(message.sent_at), 'MMM d, h:mm a') : ''}
                          </p>
                        </div>
                      </div>
                    );
                  })
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Message Input */}
              <div className="pt-3 border-t border-gray-100">
                <div className="flex space-x-2">
                  <input
                    type="text"
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                    placeholder="Type a message..."
                    className="input-field flex-1 py-2"
                    disabled={sendingMessage}
                  />
                  <button
                    onClick={handleSendMessage}
                    disabled={!newMessage.trim() || sendingMessage}
                    className="btn-primary py-2 px-4"
                  >
                    <Send className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
        )}
      </div>

      {/* Apply / Application Status */}
      {!request.application_status && !request.has_applied && (request.status === 'pending' || request.status === 'active') && (
        <div className="card">
          <div className="flex items-end gap-3">
            <div className="flex-1">
              <input
                type="text"
                value={applicationMessage}
                onChange={(e) => setApplicationMessage(e.target.value)}
                placeholder="Add a message to the owner (optional)"
                className="input-field w-full py-2 text-sm"
              />
            </div>
            <button
              onClick={handleApply}
              disabled={applying}
              className="btn-primary py-2 px-5"
            >
              {applying ? 'Sending...' : 'Apply'}
            </button>
          </div>
        </div>
      )}

      {(request.application_status || request.has_applied) && request.status !== 'matched' && request.status !== 'completed' && (
        <div className="flex items-center gap-2 px-1 text-sm">
          {request.application_status === 'selected' ? (
            <>
              <CheckCircle className="h-4 w-4 text-green-600" />
              <span className="text-green-700 font-medium">
                {request.request_category === 'position'
                  ? 'Selected — the captain/owner picked you up for an interview for this position'
                  : 'Selected — the owner chose you for this job'}
              </span>
            </>
          ) : (
            <>
              <Clock className="h-4 w-4 text-navy-400" />
              <span className="text-navy-500">Application pending — waiting for captain/owner review</span>
            </>
          )}
        </div>
      )}

      {/* Mark Complete / Withdraw Section for matched requests */}
      {request.status === 'matched' && request.application_status === 'selected' && (
        <div className="card bg-blue-50 border border-blue-200">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-semibold text-blue-900">Job Ready to Complete?</h3>
              <p className="text-sm text-blue-700 mt-1">
                {request.helper_completed
                  ? 'You marked this complete. Waiting for owner to confirm.'
                  : request.request_category === 'position'
                    ? 'Once your mission is complete, mark this job as done.'
                    : 'Once you\'ve completed the mooring assistance, mark this job as complete.'}
              </p>
            </div>
            <div className="flex items-center gap-3">
              {!request.helper_completed && (
                <>
                  <button
                    onClick={() => setShowWithdrawModal(true)}
                    disabled={actionLoading}
                    className="text-red-600 hover:text-red-700 font-medium text-sm"
                  >
                    Withdraw
                  </button>
                  <button
                    onClick={handleMarkComplete}
                    disabled={actionLoading}
                    className="btn-primary"
                  >
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Mark Complete
                  </button>
                </>
              )}
              {request.helper_completed && (
                <span className="inline-flex items-center text-blue-600 font-medium">
                  <CheckCircle className="h-5 w-5 mr-1" />
                  Waiting for owner
                </span>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Completed Status */}
      {request.status === 'completed' && (
        <div className="card bg-green-50 border border-green-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <CheckCircle className="h-8 w-8 text-green-600 mr-3" />
              <div>
                <h3 className="font-semibold text-green-900">Job Completed!</h3>
                <p className="text-sm text-green-700">
                  {request.request_category === 'position'
                    ? 'This mission has been completed.'
                    : 'This mooring assistance has been completed.'}
                </p>
              </div>
            </div>
            {!request.helper_rated && (
              <button
                onClick={() => setShowRatingModal(true)}
                className="btn-primary"
              >
                <Star className="h-4 w-4 mr-2" />
                Rate Owner
              </button>
            )}
          </div>
          {request.received_rating && (
            <div className="mt-4 pt-4 border-t border-green-200">
              <div className="flex items-center gap-1 mb-1">
                {[1, 2, 3, 4, 5].map((s) => (
                  <Star key={s} className={`h-4 w-4 ${s <= request.received_rating.rating ? 'text-gold-500 fill-current' : 'text-gray-300'}`} />
                ))}
                <span className="text-sm text-green-800 ml-1">({request.received_rating.rating}.0)</span>
              </div>
              {request.received_rating.comment && (
                <p className="text-sm text-green-800 italic">"{request.received_rating.comment}"</p>
              )}
              <p className="text-xs text-green-600 mt-1">
                — {request.received_rating.first_name} {request.received_rating.last_name}, {format(new Date(request.received_rating.created_at), 'MMM d, yyyy')}
              </p>
            </div>
          )}
        </div>
      )}

      {/* Rating Modal */}
      {showRatingModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4 shadow-2xl">
            <h2 className="text-xl font-display font-semibold text-navy-900 mb-4">
              {request.status === 'matched' ? 'Complete Job & Rate Owner' : 'Rate the Owner'}
            </h2>
            <p className="text-navy-600 mb-4">
              {request.status === 'matched'
                ? 'Please rate the owner to complete this job. Your rating will be visible to the owner only after they also rate you.'
                : 'How was your experience with this job?'}
            </p>

            {/* Star Rating */}
            <div className="flex justify-center space-x-2 mb-6">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  onClick={() => setRating(star)}
                  className="focus:outline-none"
                >
                  <Star
                    className={`h-10 w-10 transition-colors ${
                      star <= rating
                        ? 'text-gold-500 fill-current'
                        : 'text-gray-300'
                    }`}
                  />
                </button>
              ))}
            </div>

            {/* Comment */}
            <textarea
              value={ratingComment}
              onChange={(e) => setRatingComment(e.target.value)}
              placeholder="Add a comment (optional)"
              className="input-field w-full mb-4"
              rows={3}
            />

            {/* Actions */}
            <div className="flex space-x-3">
              <button
                onClick={() => {
                  setShowRatingModal(false);
                  setRating(0);
                  setRatingComment('');
                }}
                className="btn-secondary flex-1"
              >
                Cancel
              </button>
              <button
                onClick={request.status === 'matched' ? handleCompleteAndRate : handleSubmitRating}
                disabled={rating === 0 || submittingRating}
                className="btn-primary flex-1"
              >
                {submittingRating ? 'Submitting...' : (request.status === 'matched' ? 'Complete & Rate' : 'Submit Rating')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Withdraw Confirmation Modal */}
      {showWithdrawModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4 shadow-2xl">
            <h2 className="text-xl font-display font-semibold text-navy-900 mb-2">
              Withdraw from Job
            </h2>
            <p className="text-navy-600 mb-4">
              Are you sure you want to withdraw? The owner will be notified and can select a different helper.
            </p>
            <textarea
              value={withdrawReason}
              onChange={(e) => setWithdrawReason(e.target.value)}
              placeholder="Reason for withdrawing (optional)"
              className="input-field w-full mb-4"
              rows={2}
            />
            <div className="flex space-x-3">
              <button
                onClick={() => { setShowWithdrawModal(false); setWithdrawReason(''); }}
                className="btn-secondary flex-1"
              >
                Cancel
              </button>
              <button
                onClick={handleWithdraw}
                disabled={withdrawing}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white font-medium py-3 px-6 rounded-lg transition-all duration-200"
              >
                {withdrawing ? 'Withdrawing...' : 'Confirm Withdraw'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* More Info Modal for Owner Profile */}
      {showMoreInfo && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 max-w-lg w-full shadow-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-display font-semibold text-navy-900">
                Owner Profile
              </h2>
              <button
                onClick={() => { setShowMoreInfo(false); setMoreInfoData(null); }}
                className="p-1 text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {loadingMoreInfo ? (
              <div className="flex items-center justify-center py-12">
                <div className="spinner w-8 h-8" />
              </div>
            ) : moreInfoData ? (
              <div className="space-y-4">
                {/* Basic Info */}
                <div className="flex items-center">
                  <div className="h-16 w-16 bg-primary-100 rounded-full flex items-center justify-center">
                    {moreInfoData.profile_photo_url ? (
                      <img src={moreInfoData.profile_photo_url} alt="" className="h-16 w-16 rounded-full object-cover" />
                    ) : (
                      <span className="text-primary-700 font-semibold text-xl">
                        {moreInfoData.first_name?.[0]}{moreInfoData.last_name?.[0]}
                      </span>
                    )}
                  </div>
                  <div className="ml-4">
                    <p className="text-lg font-medium text-navy-900">
                      {moreInfoData.first_name} {moreInfoData.last_name}
                    </p>
                    <div className="flex items-center text-sm text-navy-600 mt-1">
                      {moreInfoData.rating > 0 ? (
                        <>
                          <Star className="h-4 w-4 text-gold-500 fill-current mr-1" />
                          <span>{moreInfoData.rating.toFixed(1)}</span>
                          <span className="text-navy-400 ml-1">({moreInfoData.rating_count} reviews)</span>
                        </>
                      ) : (
                        <span className="text-navy-400">No ratings yet</span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-gray-50 rounded-lg p-3 text-center">
                    <Briefcase className="h-5 w-5 text-primary-600 mx-auto mb-1" />
                    <p className="text-lg font-semibold text-navy-900">{moreInfoData.completed_jobs || 0}</p>
                    <p className="text-xs text-navy-500">Completed Jobs</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3 text-center">
                    <UserCheck className="h-5 w-5 text-primary-600 mx-auto mb-1" />
                    <p className="text-lg font-semibold text-navy-900">{moreInfoData.months_active || 0}</p>
                    <p className="text-xs text-navy-500">Months Active</p>
                  </div>
                </div>

                {/* Yachts */}
                {moreInfoData.yachts && moreInfoData.yachts.length > 0 && (
                  <div>
                    <h3 className="text-sm font-semibold text-navy-700 mb-2 flex items-center">
                      <Ship className="h-4 w-4 mr-1" />
                      Yachts
                    </h3>
                    <div className="space-y-2">
                      {moreInfoData.yachts.map((yacht, idx) => (
                        <div key={idx} className="flex items-center justify-between bg-gray-50 rounded-lg px-3 py-2">
                          <span className="font-medium text-navy-900">{yacht.name}</span>
                          <span className="text-sm text-navy-500">
                            {yacht.yacht_type && <span className="capitalize">{yacht.yacht_type}</span>}
                            {yacht.length_m && <span className="ml-1">· {yacht.length_m}m</span>}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* References */}
                {moreInfoData.references && moreInfoData.references.length > 0 && (
                  <div>
                    <h3 className="text-sm font-semibold text-navy-700 mb-2 flex items-center">
                      <Award className="h-4 w-4 mr-1" />
                      Recent Reviews
                    </h3>
                    <div className="space-y-2">
                      {moreInfoData.references.map((ref, idx) => (
                        <div key={idx} className="bg-gray-50 rounded-lg p-3">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-sm font-medium text-navy-800">{ref.from_name}</span>
                            <div className="flex items-center">
                              <Star className="h-3 w-3 text-gold-500 fill-current" />
                              <span className="ml-0.5 text-sm text-navy-600">{ref.rating}</span>
                            </div>
                          </div>
                          <p className="text-sm text-navy-600 italic">"{ref.comment}"</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Member Since */}
                <div className="text-center text-sm text-navy-500 pt-2 border-t border-gray-100">
                  Member since {format(new Date(moreInfoData.member_since || moreInfoData.created_at), 'MMMM yyyy')}
                </div>
              </div>
            ) : (
              <p className="text-center text-navy-500 py-8">Failed to load profile</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default HelperRequestDetails;
